import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./HeatmapDashboard.css";
import "./HubNotMappedGRs.css";
import redirectURL from "../../redirectURL";

import "@ag-grid-community/all-modules/dist/styles/ag-grid.css";
import "@ag-grid-community/all-modules/dist/styles/ag-theme-balham.css";
import { AgGridReact } from "@ag-grid-community/react";
import { DashboardHeaderBanner } from "./DashboardHeaderSlot.jsx";

import { LicenseManager, AllModules } from "@ag-grid-enterprise/all-modules";
import LICENSE_KEY from "../../../common/agGridLicense.js";
import Select from "react-select";
import SweetAlert from "react-bootstrap-sweetalert";
import XLSX from "xlsx/dist/xlsx.full.min.js";
import { getHyphenDDMMMYYYYHHMM } from "../../common/utils.js";

LicenseManager.setLicenseKey(LICENSE_KEY);

// Which side has to act on each reason (drives the badge colour)
var REASON_SEVERITY = {
  WAREHOUSE_CODE_MISSING: "hnmSevData",
  CONSIGNEE_CITY_MISSING: "hnmSevData",
  MAPPING_ADDED_PENDING_REPROCESS: "hnmSevReprocess",
  MAPPING_INACTIVE: "hnmSevMaster",
  WAREHOUSE_NOT_IN_MAPPING: "hnmSevMaster",
  POSSIBLE_CITY_SPELLING: "hnmSevMaster",
  CITY_MAPPED_FOR_OTHER_WAREHOUSE: "hnmSevMaster",
  CITY_NOT_MAPPED: "hnmSevMaster",
};

// Bulk upload (same Import Data slider as the hub masters): one column "consignment_code"
var BULK_UPLOAD_HEADER = "consignment_code";
var BULK_UPLOAD_MAX = 10000;

// Unmapped GRs store the hub as null / "NONE" / "" - show it as "nan"
function hubValueOrNan(value) {
  var v = value === null || value === undefined ? "" : String(value).trim();
  return !v || v.toUpperCase() === "NONE" || v.toUpperCase() === "NAN" ? "nan" : v;
}

function formatInputDate(d) {
  var dd = String(d.getDate()).padStart(2, "0");
  var mm = String(d.getMonth() + 1).padStart(2, "0");
  return d.getFullYear() + "-" + mm + "-" + dd;
}

// yyyy-mm-dd (date input) -> dd-mm-yyyy (API)
function toApiDate(value) {
  if (!value) return "";
  var parts = value.split("-");
  return parts.length === 3 ? parts[2] + "-" + parts[1] + "-" + parts[0] : "";
}

// Three years back to today. This grid is a backlog to clear, not a recent-activity
// view, so it has to open on every unmapped GR whatever its age. The 30-day default
// it replaces showed an empty grid against a non-zero KPI on the dashboards: an
// unmapped GR is by definition one processed a while ago, so hardly any are recent.
function defaultFromDate() {
  var d = new Date();
  d.setFullYear(d.getFullYear() - 3);
  return formatInputDate(d);
}

function formatRefreshTime(date) {
  if (!date) return "Never";
  return date.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

// First sheet -> { codes, errors }; errors use the hub masters' { row, errors[] } shape
function validateGrSheet(workbook) {
  var sheet = workbook.Sheets[workbook.SheetNames[0]];
  var rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
  var header = (rows[0] || []).map(function (h) {
    return String(h).trim().toLowerCase().replace(/\s+/g, "_");
  });
  var col = header.indexOf(BULK_UPLOAD_HEADER);
  if (col === -1) {
    return {
      codes: [],
      errors: [{ row: 1, errors: ["Column '" + BULK_UPLOAD_HEADER + "' not found - use the sample template"] }],
    };
  }

  var codes = [];
  var errors = [];
  var firstRow = {};
  for (var i = 1; i < rows.length; i++) {
    var excelRow = i + 1;
    var code = String(rows[i][col] === undefined ? "" : rows[i][col]).trim();
    if (!code) {
      var rowIsBlank = rows[i].every(function (v) { return String(v).trim() === ""; });
      if (!rowIsBlank) errors.push({ row: excelRow, errors: [BULK_UPLOAD_HEADER + " is empty"] });
      continue;
    }
    if (firstRow[code]) {
      errors.push({ row: excelRow, errors: ["Duplicate GR " + code + " (same as row " + firstRow[code] + ")"] });
      continue;
    }
    firstRow[code] = excelRow;
    codes.push(code);
  }
  if (!codes.length && !errors.length) {
    errors.push({ row: 2, errors: ["No GR numbers found in the file"] });
  }
  if (codes.length > BULK_UPLOAD_MAX) {
    errors.push({ row: "-", errors: [codes.length + " GRs found - max " + BULK_UPLOAD_MAX + " per upload"] });
    codes = [];
  }
  return { codes: codes, errors: errors, rowByCode: firstRow };
}

function getShortFileName(name, maxLength) {
  maxLength = maxLength || 31;
  var lastDot = name.lastIndexOf(".");
  if (lastDot <= 0) return name.length > maxLength ? name.slice(0, maxLength) + "..." : name;
  var base = name.substring(0, lastDot);
  return base.length <= maxLength ? name : base.substring(0, maxLength) + "..." + name.substring(lastDot);
}

var selectStyles = {
  control: function (base, state) {
    return {
      ...base,
      minHeight: "32px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#1976d2" : "#cfd8dc",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(25,118,210,0.12)" : "none",
      fontSize: "12px",
    };
  },
  valueContainer: function (base) {
    return { ...base, padding: "2px 8px", maxHeight: "72px", overflowY: "auto" };
  },
  menu: function (base) {
    return { ...base, fontSize: "12px", zIndex: 9999 };
  },
  menuPortal: function (base) {
    return { ...base, zIndex: 9999 };
  },
};

export default function HubNotMappedGRs() {
  var gridRef = useRef(null);
  var fileInputRef = useRef(null);

  var [loading, setLoading] = useState(false);
  var [lastRefreshTime, setLastRefreshTime] = useState(null);
  var [rows, setRows] = useState([]);
  var [truncated, setTruncated] = useState(false);
  var [error, setError] = useState("");
  var [selectedCount, setSelectedCount] = useState(0);

  var [warehouses, setWarehouses] = useState([]);
  var [selectedWarehouse, setSelectedWarehouse] = useState([]);
  var [fromDate, setFromDate] = useState(defaultFromDate);
  var [toDate, setToDate] = useState(function () {
    return formatInputDate(new Date());
  });

  // Reprocess flow: { codes, source, summary } while the confirmation is open
  var [pending, setPending] = useState(null);
  var [working, setWorking] = useState(false);
  var [notice, setNotice] = useState(null);
  // Bulk upload slider
  var [showBulkSlider, setShowBulkSlider] = useState(false);
  var [bulkFileName, setBulkFileName] = useState("");
  var [bulkCodes, setBulkCodes] = useState([]); // GRs from the file that exist in the system
  var [bulkErrors, setBulkErrors] = useState([]);
  var [bulkChecking, setBulkChecking] = useState(false);
  var [bulkSummary, setBulkSummary] = useState(null); // dry-run result for the file's GRs
  var bulkCheckIdRef = useRef(0); // ignore a check that finishes after a newer file was chosen

  useEffect(function () {
    var isMounted = true;
    redirectURL
      .post("/msildashboard/fetchWarehousesAndHubsFrGr", {})
      .then(function (response) {
        var data = response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
        var list = data
          .map(function (item) { return item && item.warehouse ? String(item.warehouse) : ""; })
          .filter(function (w) { return w && w !== "all"; })
          .sort();
        if (isMounted) setWarehouses(list);
      })
      .catch(function (e) {
        console.error("fetchWarehousesAndHubsFrGr API failed", e);
      });
    return function () {
      isMounted = false;
    };
  }, []);

  var fetchData = useCallback(
    async function () {
      setLoading(true);
      setError("");
      try {
        var response = await redirectURL.post("/msildashboard/fetchHubNotMappedGRs", {
          warehouse: selectedWarehouse.length ? selectedWarehouse : "all",
          from_date: toApiDate(fromDate),
          to_date: toApiDate(toDate),
        });
        var data = response && response.data ? response.data : null;
        if (!data || data.status !== "success") {
          throw new Error((data && data.message) || "Failed to load hub not mapped GRs");
        }
        setRows(Array.isArray(data.records) ? data.records : []);
        setTruncated(!!data.truncated);
        setLastRefreshTime(new Date());
      } catch (e) {
        console.error("fetchHubNotMappedGRs API failed", e);
        setRows([]);
        setError(e.message || "Failed to load hub not mapped GRs");
      } finally {
        setSelectedCount(0);
        setLoading(false);
      }
    },
    [selectedWarehouse, fromDate, toDate]
  );

  useEffect(
    function () {
      fetchData();
    },
    [fetchData]
  );

  // Step 1: dry run -> confirmation dialog with what will happen
  var startReprocess = useCallback(async function (codes, source) {
    if (!codes.length) {
      setNotice({ type: "warning", title: "No GR numbers found", text: "" });
      return;
    }
    setWorking(true);
    try {
      var response = await redirectURL.post("/msildashboard/reprocessGRs", {
        consignment_codes: codes,
        dry_run: true,
        source: source,
      });
      var data = response && response.data ? response.data : null;
      if (!data || data.status !== "success") {
        throw new Error((data && data.message) || "Could not check the GRs");
      }
      setPending({ codes: codes, source: source, summary: data.summary });
    } catch (e) {
      var msg = (e.response && e.response.data && e.response.data.message) || e.message;
      setNotice({ type: "danger", title: "Could not check the GRs", text: msg });
    } finally {
      setWorking(false);
    }
  }, []);

  // Step 2: user confirmed -> set process = 0 in staging_db_bridge
  var confirmReprocess = useCallback(
    async function () {
      if (!pending) return;
      setWorking(true);
      try {
        var response = await redirectURL.post("/msildashboard/reprocessGRs", {
          consignment_codes: pending.codes,
          dry_run: false,
          source: pending.source,
          requested_by: localStorage.getItem("email") || localStorage.getItem("username") || "",
        });
        var data = response && response.data ? response.data : null;
        if (!data || data.status !== "success") {
          throw new Error((data && data.message) || "Reprocess request failed");
        }
        setPending(null);
        setNotice({
          type: "success",
          title: data.summary.found.toLocaleString("en-IN") + " GR(s) queued for reprocessing",
          text: "They will be recalculated on the next GR processing run.",
        });
        if (gridRef.current && gridRef.current.api) gridRef.current.api.deselectAll();
      } catch (e) {
        var msg = (e.response && e.response.data && e.response.data.message) || e.message;
        setPending(null);
        setNotice({ type: "danger", title: "Reprocess request failed", text: msg });
      } finally {
        setWorking(false);
      }
    },
    [pending]
  );

  var reprocessSelected = useCallback(
    function () {
      if (!gridRef.current || !gridRef.current.api) return;
      var codes = gridRef.current.api.getSelectedRows().map(function (r) {
        return r.consignment_code;
      });
      startReprocess(codes, "hub_not_mapped_selection");
    },
    [startReprocess]
  );

  // File rows -> format check, then a dry run against staging_db_bridge so GRs that are
  // not in the system are listed as row errors and only existing GRs count as valid
  var checkBulkFile = useCallback(async function (result) {
    var checkId = ++bulkCheckIdRef.current;
    setBulkSummary(null);
    setBulkCodes([]);
    setBulkErrors(result.errors);
    if (!result.codes.length) return;

    setBulkChecking(true);
    try {
      var response = await redirectURL.post("/msildashboard/reprocessGRs", {
        consignment_codes: result.codes,
        dry_run: true,
        source: "hub_not_mapped_bulk_upload",
      });
      var data = response && response.data ? response.data : null;
      if (!data || data.status !== "success") {
        throw new Error((data && data.message) || "Could not check the GRs");
      }
      if (checkId !== bulkCheckIdRef.current) return;

      var notFound = {};
      data.summary.not_found.forEach(function (code) {
        notFound[code] = true;
      });
      var notFoundErrors = result.codes
        .filter(function (code) { return notFound[code]; })
        .map(function (code) {
          return { row: result.rowByCode[code], errors: ["GR " + code + " not found in the system"] };
        });

      setBulkCodes(result.codes.filter(function (code) { return !notFound[code]; }));
      setBulkErrors(
        result.errors.concat(notFoundErrors).sort(function (a, b) {
          return (Number(a.row) || 0) - (Number(b.row) || 0);
        })
      );
      setBulkSummary(data.summary);
    } catch (e) {
      if (checkId !== bulkCheckIdRef.current) return;
      var msg = (e.response && e.response.data && e.response.data.message) || e.message;
      setBulkErrors(result.errors.concat([{ row: "-", errors: ["Could not check the GRs: " + msg] }]));
    } finally {
      if (checkId === bulkCheckIdRef.current) setBulkChecking(false);
    }
  }, []);

  var onFileChosen = useCallback(
    function (e) {
      var file = e.target.files && e.target.files[0];
      e.target.value = ""; // allow choosing the same file again
      if (!file) return;
      setBulkFileName(file.name);

      var reader = new FileReader();
      reader.onload = function (event) {
        var result;
        try {
          var workbook = XLSX.read(new Uint8Array(event.target.result), { type: "array" });
          result = validateGrSheet(workbook);
        } catch (err) {
          console.error("Bulk upload parse failed", err);
          result = {
            codes: [],
            errors: [{ row: "-", errors: ["Could not read the file - upload a .xlsx, .xls or .csv file"] }],
            rowByCode: {},
          };
        }
        checkBulkFile(result);
      };
      reader.readAsArrayBuffer(file);
    },
    [checkBulkFile]
  );

  var closeBulkSlider = useCallback(function () {
    bulkCheckIdRef.current++;
    setShowBulkSlider(false);
    setBulkFileName("");
    setBulkCodes([]);
    setBulkErrors([]);
    setBulkChecking(false);
    setBulkSummary(null);
  }, []);

  // GRs are already checked -> straight to the confirmation (only existing GRs are sent)
  var submitBulkUpload = useCallback(
    function () {
      if (!bulkSummary || !bulkCodes.length) return;
      setPending({ codes: bulkCodes, source: "hub_not_mapped_bulk_upload", summary: bulkSummary });
      closeBulkSlider();
    },
    [bulkCodes, bulkSummary, closeBulkSlider]
  );

  var handleExport = useCallback(function () {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.exportDataAsCsv({ fileName: "hub_not_mapped_grs.csv", allColumns: true });
    }
  }, []);

  var warehouseOptions = useMemo(
    function () {
      return warehouses.map(function (w) {
        return { label: w, value: w };
      });
    },
    [warehouses]
  );

  var columnDefs = useMemo(function () {
    return [
      {
        headerName: "GR No",
        field: "consignment_code",
        minWidth: 200,
        pinned: "left",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
      },
      { headerName: "WRHS", field: "consigner_code", minWidth: 110 },
      { headerName: "Dealer Code", field: "consignee_code", minWidth: 130 },
      { headerName: "Dealer Name", field: "consignee_name", minWidth: 200 },
      { headerName: "Consignee City", field: "consignee_city", minWidth: 140 },
      {
        headerName: "Hub Location Code",
        colId: "hub_location_code",
        minWidth: 150,
        valueGetter: function (p) {
          return hubValueOrNan(p.data && p.data.hub_code);
        },
      },
      {
        headerName: "Hub Location Name",
        colId: "hub_location_name",
        minWidth: 160,
        valueGetter: function (p) {
          return hubValueOrNan(p.data && p.data.hub_location);
        },
      },
      {
        headerName: "Reason",
        field: "reason",
        minWidth: 280,
        cellRendererFramework: function (params) {
          if (!params.data) return null;
          return (
            <span className={"hnmBadge " + (REASON_SEVERITY[params.data.reason_code] || "")}>
              {params.value}
            </span>
          );
        },
      },
      { headerName: "Action Required", field: "action", minWidth: 420, tooltipField: "action" },
      {
        headerName: "MSIL Gateout Date & Time",
        field: "gate_out_time",
        minWidth: 190,
        filter: "agDateColumnFilter",
        valueFormatter: function (p) {
          return p && p.value ? getHyphenDDMMMYYYYHHMM(p.value) : "";
        },
      },
      { headerName: "Truck No", field: "truck_no", minWidth: 130 },
      {
        headerName: "GR Volume",
        field: "volume",
        minWidth: 110,
        filter: "agNumberColumnFilter",
        valueFormatter: function (p) {
          var n = Number(p.value);
          return p.value === null || p.value === undefined || isNaN(n) ? "" : n.toFixed(1);
        },
      },
      { headerName: "Reason Code", field: "reason_code", minWidth: 220, hide: true },
    ];
  }, []);

  var defaultColDef = useMemo(function () {
    return {
      sortable: true,
      filter: "agSetColumnFilter",
      resizable: true,
      minWidth: 100,
      floatingFilter: true,
    };
  }, []);

  var summary = pending ? pending.summary : null;

  return (
    <div className="hmPage">
      {summary ? (
        <SweetAlert
          type={summary.still_unmapped.length || summary.not_found.length ? "warning" : "info"}
          showCancel
          title="Reprocess GRs"
          confirmBtnText={
            working ? "Submitting..." : "Reprocess " + summary.found.toLocaleString("en-IN") + " GR(s)"
          }
          cancelBtnText="Cancel"
          confirmBtnBsStyle="primary"
          disabled={working || !summary.found}
          onConfirm={confirmReprocess}
          onCancel={function () {
            if (!working) setPending(null);
          }}
          style={{ width: "580px" }}
        >
          <div className="hnmConfirm">
            <p className="hnmLead">
              {summary.found.toLocaleString("en-IN")} GR(s) will be queued and recalculated (hub, cost and hub
              operator) on the next processing run.
            </p>

            <div className="hnmStats">
              <div className="hnmStat hnmStatOk">
                <div className="hnmStatValue">{summary.will_get_hub.toLocaleString("en-IN")}</div>
                <div className="hnmStatLabel">Ready - will get a hub</div>
              </div>
              <div className="hnmStat hnmStatWarn">
                <div className="hnmStatValue">
                  {summary.still_unmapped
                    .reduce(function (n, r) { return n + r.count; }, 0)
                    .toLocaleString("en-IN")}
                </div>
                <div className="hnmStatLabel">Need master fix</div>
              </div>
              <div className="hnmStat hnmStatMuted">
                <div className="hnmStatValue">{summary.not_found.length.toLocaleString("en-IN")}</div>
                <div className="hnmStatLabel">Skipped - not found</div>
              </div>
            </div>

            {summary.still_unmapped.length ? (
              <div className="hnmCallout">
                <div className="hnmCalloutTitle">
                  {summary.will_get_hub
                    ? "Some GRs will stay unmapped until the master is fixed:"
                    : "None of these GRs will get a hub yet - fix the master first:"}
                </div>
                <ul>
                  {summary.still_unmapped.map(function (r) {
                    return (
                      <li key={r.reason_code}>
                        {r.reason} <strong>({r.count.toLocaleString("en-IN")})</strong>
                      </li>
                    );
                  })}
                </ul>
                <div className="hnmCalloutHint">
                  Update the hub-city mapping master, then reprocess. You can still reprocess now if the master is
                  being fixed in the meantime.
                </div>
              </div>
            ) : null}

            {summary.already_mapped || summary.not_processed_yet ? (
              <p className="hnmNote">
                {summary.already_mapped
                  ? summary.already_mapped.toLocaleString("en-IN") + " GR(s) already have a hub and will be recalculated. "
                  : ""}
                {summary.not_processed_yet
                  ? summary.not_processed_yet.toLocaleString("en-IN") + " GR(s) are not processed yet."
                  : ""}
              </p>
            ) : null}

            {summary.not_found.length ? (
              <p className="hnmNote">
                Not found: {summary.not_found.slice(0, 10).join(", ")}
                {summary.not_found.length > 10 ? " ..." : ""}
              </p>
            ) : null}
          </div>
        </SweetAlert>
      ) : null}

      {notice ? (
        <SweetAlert
          type={notice.type}
          title={notice.title}
          onConfirm={function () {
            setNotice(null);
          }}
        >
          {notice.text}
        </SweetAlert>
      ) : null}

      {showBulkSlider && <div className="slider-overlay" onClick={closeBulkSlider} />}

      <div className={`hub-slider ${showBulkSlider ? "open" : ""} d-flex flex-column`}>
        <div className="slider-header">
          Import Data - Reprocess GRs
          <span style={{ cursor: "pointer" }} onClick={closeBulkSlider}>✕</span>
        </div>

        <div className="slider-body flex-grow-1">
          <p className="m-0" style={{ fontSize: "13px", color: "#546e7a" }}>
            Upload the GR numbers ({BULK_UPLOAD_HEADER}) to reprocess. Their process flag is set to 0 and they
            are recalculated on the next processing run.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={onFileChosen}
            style={{ display: "none" }}
          />
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-info d-flex justify-content-center p-3"
              type="button"
              onClick={function () {
                if (fileInputRef.current) fileInputRef.current.click();
              }}
            >
              Upload File
            </button>
            <a
              href={require("../../../assets/gr_reprocess_template.xlsx")}
              download="gr_reprocess_template.xlsx"
              className="btn btn-secondary d-flex justify-content-center ml-3 p-3 flex-grow-1"
            >
              Download Sample Template
            </a>
          </div>

          {bulkFileName && (
            <div style={{ marginTop: "15px", display: "flex", gap: "6px", maxWidth: "410px" }}>
              <span>Selected File :</span>
              <span
                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, fontWeight: "bold" }}
                title={bulkFileName}
              >
                {getShortFileName(bulkFileName)}
              </span>
            </div>
          )}
          {bulkChecking && <div className="text-info">Checking GRs against the system...</div>}
          {bulkFileName && bulkSummary && !bulkChecking && (
            <div className={bulkCodes.length ? "text-success" : "text-danger"}>
              {bulkCodes.length.toLocaleString("en-IN")} of {bulkSummary.requested.toLocaleString("en-IN")} GR(s)
              found in the system
              {bulkSummary.not_found.length
                ? " - " + bulkSummary.not_found.length.toLocaleString("en-IN") + " not in the system (skipped)"
                : ""}
            </div>
          )}

          {bulkErrors.length > 0 && (
            <>
              <h6 className="text-danger mt-3">Validation Errors</h6>
              <div style={{ flexGrow: 1, overflowY: "auto", width: "100%" }}>
                <table className="table-bordered mb-0 err-table" style={{ width: "100%" }}>
                  <thead className="table-light text-center" style={{ zIndex: "100", backgroundColor: "#efefef" }}>
                    <tr>
                      <th style={{ width: "60px", padding: "9px 15px" }}>Row</th>
                      <th>Error Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkErrors.map(function (err, i) {
                      return (
                        <tr key={i}>
                          <td className="text-center" style={{ padding: "9px 15px" }}>{err.row}</td>
                          <td style={{ padding: "9px 15px", wordBreak: "break-word" }}>
                            {err.errors.map(function (msg, idx) {
                              return <p className="m-0" key={idx}>{msg}</p>;
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="slider-footer">
          <button
            className="save-btn"
            style={{
              margin: "0px",
              width: "auto",
              padding: "5px 25px",
              cursor: !bulkCodes.length || bulkChecking || working ? "not-allowed" : "pointer",
              opacity: !bulkCodes.length || bulkChecking || working ? "0.5" : "1",
            }}
            onClick={submitBulkUpload}
            disabled={!bulkCodes.length || bulkChecking || working}
          >
            {bulkErrors.length && bulkCodes.length ? "Submit only valid data" : "Submit"}
          </button>
        </div>
      </div>

      <div className="hmSection">
        <DashboardHeaderBanner>
          <div className="hmHeader">
            <div className="hmTitleAccent hmAccentOrange">
              <h2 className="hmTitle">Hub Not Mapped GRs</h2>
            </div>

            <div className="hmRefresh">
              Last refreshed: {formatRefreshTime(lastRefreshTime)}
              {loading ? <span style={{ marginLeft: 10 }}>(Loading...)</span> : null}
            </div>
          </div>
        </DashboardHeaderBanner>

        <div className="hmCard">
          <div className="hmCardHeader">
            <div className="hmCardTitle">Filters</div>
          </div>
          <div className="hmCardBody hmCardBodyCompact">
            <div className="hmFilterGrid">
              <div className="hmField">
                <label className="hmLabel" htmlFor="hnm-from-date">From Date</label>
                <input
                  id="hnm-from-date"
                  className="hmInput"
                  type="date"
                  value={fromDate}
                  max={toDate}
                  onChange={function (e) {
                    setFromDate(e.target.value);
                  }}
                />
              </div>
              <div className="hmField">
                <label className="hmLabel" htmlFor="hnm-to-date">To Date</label>
                <input
                  id="hnm-to-date"
                  className="hmInput"
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={function (e) {
                    setToDate(e.target.value);
                  }}
                />
              </div>
              <div className="hmField">
                <label className="hmLabel" htmlFor="hnm-warehouse">Warehouse</label>
                <Select
                  inputId="hnm-warehouse"
                  classNamePrefix="hmReactSelect"
                  options={warehouseOptions}
                  value={warehouseOptions.filter(function (o) {
                    return selectedWarehouse.indexOf(o.value) !== -1;
                  })}
                  onChange={function (selected) {
                    setSelectedWarehouse(
                      (selected || []).map(function (o) {
                        return o.value;
                      })
                    );
                  }}
                  placeholder="All"
                  isMulti={true}
                  isSearchable={true}
                  closeMenuOnSelect={false}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="hmField hmFieldBtn" style={{ display: "flex", gap: 8 }}>
                <button className="hmBtn hmBtnOutline" type="button" onClick={fetchData} disabled={loading}>
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="hmCard">
          <div className="hmCardHeader hnmGridHeader">
            <div>
              <div className="hmCardTitle">Hub Not Mapped GR Details</div>
              <div className="hmSubText">
                Total: <strong>{rows.length.toLocaleString("en-IN")}</strong> GRs
                {selectedCount ? " | Selected: " + selectedCount.toLocaleString("en-IN") : ""}
                {truncated ? " | Limited to the latest 50,000 GRs - narrow the date range" : ""}
              </div>
            </div>
            <div className="hnmActions">
              <button
                type="button"
                className="hmBtn"
                onClick={reprocessSelected}
                disabled={!selectedCount || working}
                title="Fix the hub-city mapping master first, then reprocess the GRs"
              >
                Reprocess GRs{selectedCount ? " (" + selectedCount + ")" : ""}
              </button>
              <button
                type="button"
                className="hmBtn hmBtnOutline"
                onClick={function () {
                  setShowBulkSlider(true);
                }}
                disabled={working}
              >
                Bulk Upload GRs
              </button>
              <button type="button" className="hmBtn hmBtnOutline" onClick={handleExport} disabled={!rows.length}>
                Export
              </button>
            </div>
          </div>
          <div className="hmCardBody">
            {error ? <div className="hmSubText" style={{ color: "#c62828", marginBottom: 8 }}>{error}</div> : null}
            <div className="ag-theme-balham" style={{ height: "66vh", width: "100%" }}>
              <AgGridReact
                modules={AllModules}
                ref={gridRef}
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowSelection="multiple"
                suppressRowClickSelection={true}
                onSelectionChanged={function (params) {
                  setSelectedCount(params.api.getSelectedRows().length);
                }}
                pagination={true}
                paginationPageSize={100}
                headerHeight={45}
                rowHeight={40}
                enableRangeSelection={true}
                enableBrowserTooltips={true}
                overlayNoRowsTemplate={loading ? "Loading..." : "No hub not mapped GRs for the selected filters"}
                sideBar={{
                  toolPanels: [
                    {
                      id: "columns",
                      labelDefault: "Columns",
                      labelKey: "columns",
                      iconKey: "columns",
                      toolPanel: "agColumnsToolPanel",
                      toolPanelParams: { suppressPivotMode: true, suppressValues: true },
                    },
                    {
                      id: "filters",
                      labelDefault: "Filters",
                      labelKey: "filters",
                      iconKey: "filter",
                      toolPanel: "agFiltersToolPanel",
                    },
                  ],
                }}
                getContextMenuItems={function () {
                  return ["copy", "copyWithHeaders", "csvExport", "excelExport"];
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
